import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  ContactGroupAssignMode,
  ContactGrouopService,
} from 'src/app/private/services/contact-groups.service';
import { LeadService } from 'src/app/private/services/lead.service';
import { TrackingService } from 'src/app/private/services/tracking.service';
import { ContactGroupOption } from 'src/app/utils/contact-group-batch.util';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import {
  CONTACT_GROUP_TYPE_OPTIONS,
  ContactGroupType,
} from 'src/app/utils/contact-group-type.util';

@Component({
  selector: 'app-convert-to-group',
  templateUrl: './convert-to-group.component.html',
  styleUrls: ['./convert-to-group.component.scss'],
})
export class ConvertToGroupComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  @Output() reload = new EventEmitter<void>();
  @Input() currentSelectedLeadSpaceId: any;
  @Input() currentSelectedLeadSpaceName: any;

  btnLoading = false;
  groupsLoading = false;
  showNewGroupForm = false;
  contactGroups: ContactGroupOption[] = [];
  selectedGroupId: number | null = null;
  groupName = '';
  group_type: ContactGroupType = 'both';
  groupTypeOptions = CONTACT_GROUP_TYPE_OPTIONS;
  leadCount: number | null = null;
  isAdmin = false;

  constructor(
    private toaster: ToastrService,
    private api: LeadService,
    private contactGroupService: ContactGrouopService,
    private localstorage: LocalStorageService,
    private tracker: TrackingService
  ) {
    this.isAdmin = this.localstorage.checkFullAccessForTableList(
      this.localstorage.getRole()
    );
  }

  ngOnInit(): void {
    this.loadLeadCount();
    this.loadContactGroups();
  }

  get groupMode(): ContactGroupAssignMode {
    return this.showNewGroupForm ? 'new' : 'existing';
  }

  get submitLabel(): string {
    if (!this.btnLoading) {
      return this.showNewGroupForm ? 'Create Group' : 'Add to Group';
    }
    return this.showNewGroupForm ? 'Creating...' : 'Adding to group...';
  }

  get canSubmit(): boolean {
    if (this.btnLoading || (this.leadCount ?? 0) < 1) {
      return false;
    }
    if (this.showNewGroupForm) {
      return !!this.groupName.trim();
    }
    return !!this.selectedGroupId && !this.groupsLoading;
  }

  closeModal(): void {
    this.close.emit();
  }

  openNewGroupForm(): void {
    this.showNewGroupForm = true;
    this.groupName = '';
  }

  useExistingGroup(): void {
    this.showNewGroupForm = false;
    if (!this.selectedGroupId && this.contactGroups.length) {
      this.selectedGroupId = this.contactGroups[0].id;
    }
  }

  private loadContactGroups(): void {
    this.groupsLoading = true;
    this.contactGroupService
      .fetchContactGroupsForSelect({
        clientId: this.localstorage.getClientId(),
        centerId: this.localstorage.getCenterId(),
        userId: this.localstorage.getUserId(),
        isAdmin: this.isAdmin,
      })
      .subscribe({
        next: (groups) => {
          this.contactGroups = groups;
          this.groupsLoading = false;
          this.showNewGroupForm = false;
          this.selectedGroupId = groups.length ? groups[0].id : null;
        },
        error: () => {
          this.groupsLoading = false;
          this.showNewGroupForm = false;
          this.selectedGroupId = null;
          this.toaster.error('Failed to load contact groups.');
        },
      });
  }

  private loadLeadCount(): void {
    if (!this.currentSelectedLeadSpaceId) {
      return;
    }

    this.api
      .getAllData({
        where: {
          client_id: this.localstorage.getClientId(),
          lead_space_id: this.currentSelectedLeadSpaceId,
        },
        filter: null,
        page: 0,
        limit: 1,
        order: {
          col: '"leadsrecord"."updatedAt"',
          dir: 'DESC',
        },
      })
      .subscribe({
        next: (res: any) => {
          this.leadCount = res?.count ?? 0;
        },
        error: () => {
          this.leadCount = 0;
        },
      });
  }

  submitConvertLeadSpace(): void {
    if ((this.leadCount ?? 0) < 1) {
      this.toaster.error('No leads found in this lead space.');
      return;
    }

    if (!this.showNewGroupForm && !this.selectedGroupId) {
      this.toaster.error('Please select a contact group.');
      return;
    }

    if (this.showNewGroupForm && !this.groupName.trim()) {
      this.toaster.error('Please enter a Contact Group Name.');
      return;
    }

    this.btnLoading = true;

    const payload: any = {
      client_id: this.localstorage.getClientId(),
      lead_space_id: this.currentSelectedLeadSpaceId,
    };

    const centerId = this.localstorage.getCenterId();
    if (centerId != null && centerId !== '' && centerId !== 'null') {
      payload.center_id = centerId;
    }

    if (!this.isAdmin) {
      payload.user_id = this.localstorage.getUserId();
    }

    const selectedGroup = this.contactGroups.find(
      (g) => g.id === this.selectedGroupId
    );
    const targetGroupName = this.showNewGroupForm
      ? this.groupName.trim()
      : selectedGroup?.name || 'group';

    if (this.showNewGroupForm) {
      payload.contact_group_name = this.groupName.trim();
      payload.contact_group_list_name = this.groupName.trim();
      payload.group_type = this.group_type;
    } else {
      payload.contact_group_id = this.selectedGroupId;
    }

    this.api.convertLeadSpaceToGroup(payload).subscribe({
      next: () => {
        const action = this.showNewGroupForm ? 'created from' : 'added to';
        this.toaster.success(
          `Lead space "${this.currentSelectedLeadSpaceName}" ${action} contact group "${targetGroupName}"!`
        );
        this.tracker.track(
          'Create',
          'Contact Group',
          `${this.showNewGroupForm ? 'Created' : 'Added'} lead space "${this.currentSelectedLeadSpaceName}" in group "${targetGroupName}"`
        );
        this.btnLoading = false;
        this.reload.emit();
        this.closeModal();
      },
      error: (err) => {
        this.toaster.error(
          err?.error?.message || 'Failed to save leads to contact group.'
        );
        this.btnLoading = false;
      },
    });
  }
}
